import React, { useRef } from 'react';
import styled from 'styled-components';
import { UseFormRegister } from 'react-hook-form';
import { RawFormFillerData } from 'shared-utils/types/formFiller';
import { WithOptionalTooltip } from './WithOptionalTooltip';
import { FieldState } from '../../../lib/formFiller';
import { StyledCheckbox } from '../styles';
import { getTabIndex } from '../../../lib/formFiller';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  // Allow hover to pass through to the styled checkbox
  pointer-events: none;
  width: 100%;
  height: 100%;
  &:focus {
    outline: none;
  }
`;

const Checkmark = styled.span`
  font-size: 75%;
  font-weight: 900;
  line-height: 1;
  position: absolute;
  margin: auto;
  z-index: 1000;
`;

/**
 * Checkbox input field component for PDF forms
 */
export function CheckboxFieldInput({
  fieldState,
  isChecked,
  register,
}: {
  fieldState: FieldState;
  isChecked: boolean;
  register: UseFormRegister<RawFormFillerData>;
}) {
  const hiddenRef = useRef<HTMLInputElement | null>(null);
  const registerProps = register(fieldState.id);

  const hiddenElement = (
    <HiddenCheckbox
      {...registerProps}
      ref={(element) => {
        // Set both the manual ref and the register ref
        hiddenRef.current = element;
        registerProps.ref(element);
      }}
      type="checkbox"
      disabled={fieldState.isReadOnly}
      tabIndex={-1}
    />
  );

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (fieldState.isReadOnly) return;
    event.preventDefault();
    hiddenRef.current?.click();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (fieldState.isReadOnly) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      hiddenRef.current?.click();
    }
  };

  return (
    <InnerCheckbox
      fieldState={fieldState}
      hiddenElement={hiddenElement}
      isChecked={isChecked}
      onKeyDown={handleKeyDown}
      onClick={onClick}
    />
  );
}

export function InnerCheckbox({
  fieldState,
  hiddenElement,
  onKeyDown,
  isChecked,
  onClick,
}: {
  fieldState: FieldState;
  hiddenElement: React.ReactNode;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onClick: (event: React.MouseEvent<HTMLInputElement>) => void;
  isChecked: boolean;
}) {
  return (
    <Container>
      {hiddenElement}
      <WithOptionalTooltip fieldState={fieldState}>
        <StyledCheckbox
          tabIndex={getTabIndex(fieldState)}
          $checked={isChecked}
          $isUnassigned={!fieldState.isAssigned}
          onKeyDown={onKeyDown}
          onClick={onClick}
        >
          {isChecked && <Checkmark>✓</Checkmark>}
        </StyledCheckbox>
      </WithOptionalTooltip>
    </Container>
  );
}
